<style lang="scss" src="./personal-information-editing-drawer.scss"></style>

<template>
  <a-tooltip
    title="Редактировать"
    placement="bottom"
    overlay-class-name="general-information-editing-tooltip"
  >
    <button
      type="button"
      class="general-information__personal-information-editing-btn"
      aria-label="Редактировать личные данные"
      @click="openDrawer"
    >
      <img
        :src="`${ICONS_BASE}/edit.svg`"
        alt=""
        class="general-information__personal-information-editing-icon"
      />
    </button>
  </a-tooltip>

  <a-drawer
    :width="800"
    :open="open"
    class="personal-information-editing-drawer"
    :body-style="{ padding: '0px 40px 32px 40px' }"
    :footer-style="{ textAlign: 'left', padding: '20px 40px 40px 40px' }"
    :header-style="{ padding: '32px 40px', border: 'none' }"
    @close="handleClose"
  >
    <a-divider class="personal-information-editing-drawer__divider-header" />

    <template #title>
      <span class="personal-information-editing-drawer__title">Личные данные</span>
    </template>

    <template #closeIcon>
      <span class="personal-information-editing-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <a-form
      ref="drawerFormRef"
      class="personal-information-editing-drawer__form"
      :model="form"
      :rules="rules"
      layout="vertical"
    >
      <div class="personal-information-editing-drawer__section">
        <span class="personal-information-editing-drawer__section--section-title">Общее</span>

        <div class="personal-information-editing-drawer__section--section-content">
          <a-form-item class="stc-ant-field-skin" label="Фамилия" name="last_name">
            <a-input v-model:value="form.last_name" placeholder="Полное название" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Имя" name="first_name">
            <a-input v-model:value="form.first_name" placeholder="Полное название" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Отчество" name="middle_name">
            <a-input v-model:value="form.middle_name" placeholder="Полное название" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Должность" name="position">
            <a-input v-model:value="form.position" placeholder="Название должности" />
          </a-form-item>
        </div>
      </div>

      <a-divider class="personal-information-editing-drawer__divider" />

      <div class="personal-information-editing-drawer__section">
        <span class="personal-information-editing-drawer__section--section-title">Контакты</span>

        <div class="personal-information-editing-drawer__section--section-content">
          <a-form-item class="stc-ant-field-skin" label="Телефон" name="phone" required>
            <a-input v-model:value="form.phone" placeholder="+7 (___) ___-__-__" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Email" name="email" required>
            <a-input v-model:value="form.email" placeholder="user@example.com" />
          </a-form-item>
        </div>
      </div>
    </a-form>

    <template #footer>
      <a-row align="middle" justify="end" class="personal-information-editing-drawer__toolbar">
        <a-col>
          <a-space>
            <button type="button" class="btn btn-outline btn-medium" @click="handleClose">
              Отмена
            </button>
            <button
              type="button"
              class="btn btn-primary btn-medium"
              :disabled="isSaveDisabled"
              @click="onSubmit"
            >
              Сохранить
            </button>
          </a-space>
        </a-col>
      </a-row>
    </template>
  </a-drawer>
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { ICONS_BASE, useEditingDrawer } from './composables/use-editing-drawer';

const { open, showDrawer, onClose: closeDrawer } = useEditingDrawer();

const drawerFormRef = ref<FormInstance>();
const isSubmitting = ref(false);
const isLoading = ref(false);
const PERSONAL_FORM_URL = getApiUrl('/api/form/personal/');

type PersonalFormResponse = {
  status: 'success' | 'error';
  msg:
    | {
        id?: string | number;
        name?: string;
        last_name?: string;
        second_name?: string;
        work_position?: string;
        email?: string;
        phone?: string;
      }
    | string;
};

const initialFormState = () => ({
  last_name: '',
  first_name: '',
  middle_name: '',
  position: '',
  phone: '',
  email: '',
});

type PersonalFormState = ReturnType<typeof initialFormState>;

const form = reactive(initialFormState());
const savedFormState = ref<PersonalFormState | null>(null);

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () => isNotBlankString(form.phone) && isNotBlankString(form.email),
);

function captureFormState(): PersonalFormState {
  return {
    last_name: form.last_name,
    first_name: form.first_name,
    middle_name: form.middle_name,
    position: form.position,
    phone: form.phone,
    email: form.email,
  };
}

function isSameFormState(current: PersonalFormState, initial: PersonalFormState): boolean {
  return (
    current.last_name === initial.last_name &&
    current.first_name === initial.first_name &&
    current.middle_name === initial.middle_name &&
    current.position === initial.position &&
    current.phone === initial.phone &&
    current.email === initial.email
  );
}

const hasUnsavedChanges = computed(() => {
  const initial = savedFormState.value;
  if (!initial) return false;

  return !isSameFormState(captureFormState(), initial);
});

const isSaveDisabled = computed(
  () =>
    isLoading.value ||
    isSubmitting.value ||
    !isRequiredFieldsFilled.value ||
    !isValidEmail(form.email) ||
    !hasUnsavedChanges.value,
);

const rules: Record<string, Rule[]> = {
  last_name: [{ message: 'Введите фамилию', whitespace: true }],
  first_name: [{ message: 'Введите имя', whitespace: true }],
  middle_name: [{ message: 'Введите отчество', whitespace: true }],
  position: [{ message: 'Введите должность', whitespace: true }],
  phone: [{ required: true, message: 'Введите телефон', whitespace: true }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  savedFormState.value = null;
  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const applyPersonalData = (data: PersonalFormResponse['msg']) => {
  if (!data || typeof data === 'string') return;

  form.last_name = data.last_name ?? '';
  form.first_name = data.name ?? '';
  form.middle_name = data.second_name ?? '';
  form.position = data.work_position ?? '';
  form.email = data.email ?? '';
  form.phone = data.phone ?? '';
};

function setPersonalFieldValue(label: string, value: string): void {
  const sections = document.querySelectorAll<HTMLElement>('.general-information__section');

  sections.forEach((section) => {
    const title = section.querySelector('.general-information__section-title');
    if (title?.textContent?.trim() !== 'Контактное лицо') return;

    section.querySelectorAll<HTMLElement>('.general-information__field').forEach((field) => {
      const labelEl = field.querySelector('.general-information__field-label');
      const valueEl = field.querySelector('.general-information__field-value');

      if (!labelEl || !valueEl) return;
      if (labelEl.textContent?.trim() !== label) return;

      valueEl.textContent = value;
    });
  });
}

function syncPersonalDataToPage(): void {
  const fullName = [form.last_name, form.first_name, form.middle_name].filter(Boolean).join(' ');

  setPersonalFieldValue('ФИО', fullName);
  setPersonalFieldValue('Должность', form.position);
  setPersonalFieldValue('Телефон', form.phone);
  setPersonalFieldValue('Email', form.email);

  const sidebarEmail = document.querySelector('.residency-lk-sidebar__desc');
  if (sidebarEmail) {
    sidebarEmail.textContent = form.email;
  }
}

const loadPersonalData = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(PERSONAL_FORM_URL, {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as PersonalFormResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось загрузить личные данные',
      );
    }

    applyPersonalData(result.msg);
    savedFormState.value = captureFormState();
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const openDrawer = async () => {
  showDrawer();
  await loadPersonalData();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) return;

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  isSubmitting.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const body = new FormData();
    body.append('last_name', form.last_name);
    body.append('first_name', form.first_name);
    body.append('middle_name', form.middle_name);
    body.append('position', form.position);
    body.append('email', form.email);
    body.append('phone', form.phone);
    body.append('sessid', sessid);

    const response = await fetch(PERSONAL_FORM_URL, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        'X-Bitrix-Csrf-Token': sessid,
      },
    });
    const result = (await response.json()) as PersonalFormResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось сохранить личные данные',
      );
    }

    savedFormState.value = captureFormState();
    syncPersonalDataToPage();
    showStcSuccessNotification(typeof result.msg === 'string' ? result.msg : 'Данные успешно сохранены');
    closeDrawer();
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  closeDrawer();
  resetFormState();
};
</script>
